import React from "react"
import { toast } from "sonner"
import { MoreHorizontal, Plus } from "lucide-react"
import {
  useGroups,
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
  useAssignablePeople,
} from "@/lib/queries"
import type { Group } from "@/lib/types"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table"

export function GroupsPanel() {
  const { data: groups = [], isLoading, isError } = useGroups()
  const { data: people = [] } = useAssignablePeople()
  const deleteGroup = useDeleteGroup()

  const [formOpen, setFormOpen] = React.useState(false)
  const [editingGroup, setEditingGroup] = React.useState<Group | null>(null)
  const [deletingGroup, setDeletingGroup] = React.useState<Group | null>(null)

  const openCreateForm = () => {
    setEditingGroup(null)
    setFormOpen(true)
  }

  const openEditForm = (group: Group) => {
    setEditingGroup(group)
    setFormOpen(true)
  }

  const handleDelete = async () => {
    if (!deletingGroup) return
    try {
      await deleteGroup.mutateAsync(deletingGroup.id)
      toast.success("Group removed")
      setDeletingGroup(null)
    } catch {
      toast.error("Failed to remove group")
    }
  }

  if (isLoading) return <p className="text-muted-foreground py-6">Loading...</p>
  if (isError) return <p className="text-destructive py-6">Could not load groups.</p>

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreateForm}>
          <Plus className="mr-2 h-4 w-4" />
          New group
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Members</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  No groups yet.
                </TableCell>
              </TableRow>
            ) : (
              groups.map((g) => (
                <TableRow key={g.id}>
                  <TableCell className="font-medium">{g.name}</TableCell>
                  <TableCell>{g.member_count}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditForm(g)}>
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeletingGroup(g)}>
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <GroupFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        group={editingGroup}
        people={people}
      />

      <AlertDialog
        open={deletingGroup !== null}
        onOpenChange={(open) => !open && setDeletingGroup(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete group?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the{" "}
              <span className="font-medium text-foreground">{deletingGroup?.name}</span>{" "}
              group. Quizzes already assigned through it are not affected.
              {deletingGroup && deletingGroup.active_schedule_count > 0 && (
                <>
                  {" "}This stops {deletingGroup.active_schedule_count} repeating assessment{" "}
                  {deletingGroup.active_schedule_count === 1 ? "round" : "rounds"}. Past rounds
                  keep the group&apos;s name.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteGroup.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function GroupFormDialog({
  open,
  onOpenChange,
  group,
  people,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  group: Group | null
  people: { id: number; name: string; username: string; role: "staff" | "manager" }[]
}) {
  const createGroup = useCreateGroup()
  const updateGroup = useUpdateGroup()
  const [name, setName] = React.useState("")
  const [memberIds, setMemberIds] = React.useState<Set<number>>(new Set())

  React.useEffect(() => {
    if (open) {
      setName(group?.name ?? "")
      setMemberIds(new Set(group?.member_ids ?? []))
    }
  }, [open, group])

  const toggleMember = (id: number) => {
    setMemberIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (group) {
        await updateGroup.mutateAsync({ id: group.id, name, member_ids: Array.from(memberIds) })
        toast.success("Group updated")
      } else {
        await createGroup.mutateAsync({ name, member_ids: Array.from(memberIds) })
        toast.success("Group created")
      }
      onOpenChange(false)
    } catch {
      toast.error(group ? "Failed to update group" : "Failed to create group")
    }
  }

  const isPending = createGroup.isPending || updateGroup.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{group ? "Edit group" : "New group"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="group-name">Name</Label>
            <Input
              id="group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="pediatrics"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>
              Members{" "}
              {memberIds.size > 0 && (
                <span className="font-normal text-muted-foreground">
                  ({memberIds.size} selected)
                </span>
              )}
            </Label>
            {people.length === 0 ? (
              <p className="text-sm text-muted-foreground">No one to add.</p>
            ) : (
              <ScrollArea className="h-40 rounded-md border p-2">
                {people.map((person) => (
                  <label
                    key={person.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                  >
                    <input
                      type="checkbox"
                      checked={memberIds.has(person.id)}
                      onChange={() => toggleMember(person.id)}
                      className="h-4 w-4 rounded border-input"
                    />
                    <span className="font-medium">{person.name}</span>
                  </label>
                ))}
              </ScrollArea>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving..." : group ? "Save changes" : "Create group"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
